import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";

import {
  Drawer,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@material-ui/core";

import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";

import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

// Icons
import DashboardIcon from "@material-ui/icons/Dashboard";
import FeaturedPlayListIcon from "@material-ui/icons/FeaturedPlayList";
import TranslateIcon from "@material-ui/icons/Translate";
import PeopleIcon from "@material-ui/icons/People";
import SettingsIcon from "@material-ui/icons/Settings";
import BookIcon from "@material-ui/icons/Book";
import FolderIcon from "@material-ui/icons/Folder";
import GTranslateIcon from "@material-ui/icons/GTranslate";
import PolicyIcon from "@material-ui/icons/Policy";
import RecentActorsIcon from "@material-ui/icons/RecentActors";
import PaymentIcon from "@material-ui/icons/Payment";
import PaymentSharpIcon from "@material-ui/icons/PaymentSharp";
import RepeatIcon from "@material-ui/icons/Repeat";
import AssessmentIcon from "@material-ui/icons/Assessment";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import { AuthContext } from "../../../context/states/authState";
import { ReactComponent as Pound } from "../../../assets/images/icons/pound.svg";
import { interpreterMenu } from "../../../menus/interpreterMenu";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

const drawerWidth = 230;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },

  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  },

  drawerClose: {
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: theme.spacing(7) + 1,

    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(7) + 1,
    },
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  },

  drawerPaper: {
    overflowX: "hidden",
  },

  menuContainer: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    overflowY: "auto",
    overflowX: "hidden",
    height: "calc(100vh - 48px)",
  },

  listItem: {
    paddingTop: 6,
    paddingBottom: 6,
  },

  nestedItem: {
    paddingLeft: theme.spacing(2),
  },

  listItemIcon: {
    minWidth: 40,
  },

  listItemText: {
    fontFamily: "Open Sans",
    fontSize: "14px",
  },

  listItemTextRoot: {
    opacity: 1,
    transition: "opacity 0.2s",
  },

  listItemTextHidden: {
    opacity: 0,
  },
  activeItem: {
    backgroundColor: "#f5f5f5",
  },
  collapseContainer: {
    background: "#1c1c1d08",
  },
}));

const iconMapper = {
  DashboardIcon: <DashboardIcon />,
  FeaturedPlayListIcon: <FeaturedPlayListIcon />,
  TranslateIcon: <TranslateIcon />,
  PeopleIcon: <PeopleIcon />,
  SettingsIcon: <SettingsIcon />,
  BookIcon: <BookIcon />,
  FolderIcon: <FolderIcon />,
  GTranslateIcon: <GTranslateIcon />,
  BusinessCenterIcon: <BusinessCenterIcon />,
  AssignmentIndOutlinedIcon: <AssignmentIndOutlinedIcon />,
  PolicyIcon: <PolicyIcon />,
  RecentActorsIcon: <RecentActorsIcon />,
  PaymentIcon: <PaymentIcon />,
  PaymentSharpIcon: <PaymentSharpIcon />,
  RepeatIcon: <RepeatIcon />,
  AssessmentIcon: <AssessmentIcon />,
  AssignmentIndIcon: <AssignmentIndIcon />,
  AccountBalanceWalletIcon: <AccountBalanceWalletIcon />,
  RecordVoiceOverIcon: <RecordVoiceOverIcon />,
  PoundIcon: <Pound style={{ width: 24, height: 24 }} />,
  AccessTimeIcon: <AccessTimeIcon />,
};

const renderIcon = (icon) => {
  if (React.isValidElement(icon)) {
    return icon;
  }

  return iconMapper[icon] || <FolderIcon />;
};

const SideDrawer = ({ matches, open, toggle }) => {
  const classes = useStyles();
  const { sidebar, currentUser } = useContext(AuthContext);

  const menuItems =
    currentUser?.role === "interpreter" ? interpreterMenu : sidebar;

  const drawerContent = (
    <>
      <div className={classes.toolbar} />
      <Divider />

      <div className={classes.menuContainer}>
        <List disablePadding>
          {menuItems?.map((item, index) => (
            <MenuItem key={item._id || index} item={item} drawerOpen={open} />
          ))}
        </List>
      </div>
    </>
  );

  if (!matches) {
    return (
      <SwipeableDrawer
        open={open}
        anchor="left"
        onClose={toggle}
        onOpen={toggle}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx(classes.drawerPaper, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

const MenuItem = ({ item, nested = false, drawerOpen }) => {
  const classes = useStyles();
  const location = useLocation();

  const children = item.children || item.items || [];

  const hasChildren = children.length > 0;

  const [open, setOpen] = useState(false);

  const routePath = item.path || item.to;

  const isActive = routePath && location.pathname.startsWith(routePath);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  if (hasChildren) {
    return (
      <>
        <ListItem
          button
          onClick={handleToggle}
          className={clsx(classes.listItem, {
            [classes.nestedItem]: nested,
            [classes.activeItem]: isActive,
          })}
        >
          <ListItemIcon className={classes.listItemIcon}>
            {renderIcon(item.icon)}
          </ListItemIcon>

          <ListItemText
            primary={item.title}
            className={clsx({
              [classes.listItemTextRoot]: drawerOpen,
              [classes.listItemTextHidden]: !drawerOpen,
            })}
            classes={{
              primary: classes.listItemText,
            }}
          />

          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>

        <Collapse
          in={open}
          timeout="auto"
          unmountOnExit
          className={classes.collapseContainer}
        >
          <List component="div" disablePadding>
            {children.map((child) => (
              <MenuItem
                key={child._id}
                item={child}
                nested={true}
                drawerOpen={drawerOpen}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItem
      button
      component={Link}
      to={item.path || item.to || "#"}
      className={clsx(classes.listItem, {
        [classes.nestedItem]: nested,
        [classes.activeItem]: isActive,
      })}
    >
      <ListItemIcon className={classes.listItemIcon}>
        {renderIcon(item.icon)}
      </ListItemIcon>

      <ListItemText
        primary={item.title}
        className={clsx({
          [classes.listItemTextRoot]: drawerOpen,
          [classes.listItemTextHidden]: !drawerOpen,
        })}
        classes={{
          primary: classes.listItemText,
        }}
      />
    </ListItem>
  );
};

SideDrawer.propTypes = {
  matches: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};

export default React.memo(SideDrawer);
